import React, { useState, useRef, useEffect, useMemo } from "react";
import { lighten } from "@mui/material";
import { DEFAULT_VISUAL_CONFIG, APPS, WORKSPACES, FROSTED_PANEL_STYLE, FROSTED_POPOVER_STYLE } from "./constants";
import { GET_IMAGE } from "../../helpers/images";
import { GMB_LOGO_URL } from "../../constants/branding";
import { UseCompanyColors } from "../../hooks/company";
import MetricDetailsModal from "./MetricDetailsModal";
import MetricSparkline from "./MetricSparkline";
import GoalProgressRing from "./GoalProgressRing";

const DEFAULT_POINTS_RULE = { metricAmount: 1, pointsEarned: 10 };

/** Company metrics rule modal: target goal with no "per day" wording */
function CompanyRuleModal({ isOpen, onClose, metric, canEdit = false, onSaveGuideText, onSaveDailyGoalText }) {
  const gameConfig = metric?.gameConfig || {};
  // Guide text / daily goal feed the TV leaderboard labels: guide text renders
  // as the grey pill under the metric name, daily goal as the teal pill.
  const savedGuideText = gameConfig.guideText ?? metric?.guideText ?? "";
  const savedDailyGoalText = gameConfig.dailyGoalText ?? metric?.dailyGoalText ?? "";
  const [guideText, setGuideText] = useState(savedGuideText);
  const [dailyGoalText, setDailyGoalText] = useState(savedDailyGoalText);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setGuideText(savedGuideText);
      setDailyGoalText(savedDailyGoalText);
      setError(null);
    }
  }, [isOpen, savedGuideText, savedDailyGoalText]);

  if (!isOpen) return null;
  const target = gameConfig.dailyTarget ?? gameConfig.target ?? 100;
  const pointsRule = gameConfig.pointsRule || DEFAULT_POINTS_RULE;
  const metricAmount = pointsRule.metricAmount ?? 1;
  const pointsEarned = pointsRule.pointsEarned ?? 10;
  const unitLabel = metric?.unit === "currency" ? "$" : "units";

  const trimmedGuide = guideText.trim();
  const trimmedGoal = dailyGoalText.trim();
  const isDirty =
    trimmedGuide !== (savedGuideText || "").trim() ||
    trimmedGoal !== (savedDailyGoalText || "").trim();

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (trimmedGuide !== (savedGuideText || "").trim() && onSaveGuideText) {
        await onSaveGuideText(metric, trimmedGuide);
      }
      if (trimmedGoal !== (savedDailyGoalText || "").trim() && onSaveDailyGoalText) {
        await onSaveDailyGoalText(metric, trimmedGoal);
      }
      onClose?.();
    } catch (err) {
      setError(err?.message || "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-900/15 backdrop-blur-[3px]" onClick={onClose}>
      <div className="rounded-lg w-full max-w-[340px] overflow-hidden p-[20px] max-h-[85vh] overflow-y-auto" style={FROSTED_PANEL_STYLE} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2.5 mb-[16px]">
          <div className="w-9 h-9 rounded-lg bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)] flex items-center justify-center flex-shrink-0">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="1" fill="currentColor" /></svg>
          </div>
          <h3 className="text-[15px] font-semibold text-gray-900">Gamification rules</h3>
        </div>
        <div className="space-y-2.5">
          <div className="px-3.5 py-[12px] bg-[var(--gmb-primary-soft)] rounded-[10px] border border-[var(--gmb-primary-tint)] flex items-center justify-between gap-[12px]">
            <span className="text-xs font-medium text-[var(--gmb-primary)]">Target goal</span>
            <span className="text-base font-semibold text-gray-900 text-right">
              {typeof target === "number" ? target.toLocaleString() : target} {unitLabel}
            </span>
          </div>
          <div className="px-3.5 py-[12px] bg-emerald-50/60 rounded-[10px] border border-emerald-100">
            <p className="text-xs font-medium text-emerald-600 mb-1">Points earned</p>
            <p className="text-[13px] text-gray-600 leading-snug">
              Every <span className="text-gray-900 font-medium">{metricAmount} {unitLabel}</span> earns <span className="text-[var(--gmb-primary)] font-medium">{pointsEarned} pts</span>
            </p>
          </div>
          <div className="px-3.5 py-[12px] bg-violet-50/60 rounded-[10px] border border-violet-100">
            <p className="text-xs font-medium text-violet-600 mb-1">Guide text</p>
            {canEdit ? (
              <>
                <input
                  type="text"
                  value={guideText}
                  onChange={(e) => setGuideText(e.target.value)}
                  placeholder="e.g. $1000 / 100 POINTS, 50 PTS / HR"
                  className="w-full bg-white border border-violet-100 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 focus:border-violet-400 focus:ring-2 focus:ring-violet-50 outline-none placeholder:text-gray-400 placeholder:font-normal"
                />
                <p className="text-[10px] text-gray-400 mt-1.5">Shown on the TV leaderboard under the metric name.</p>
              </>
            ) : (
              <p className={`text-[13px] leading-snug ${savedGuideText.trim() ? "text-gray-600" : "text-gray-400 italic"}`}>
                {savedGuideText.trim() || "No guide on how this metric works yet."}
              </p>
            )}
          </div>
          <div className="px-3.5 py-[12px] bg-teal-50/60 rounded-[10px] border border-teal-100">
            <p className="text-xs font-medium text-teal-600 mb-1">Daily goal</p>
            {canEdit ? (
              <>
                <input
                  type="text"
                  value={dailyGoalText}
                  onChange={(e) => setDailyGoalText(e.target.value)}
                  placeholder="e.g. $1,500/DAY, 3/DAY, EVERY 2 DAYS"
                  className="w-full bg-white border border-teal-100 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 focus:border-teal-500 focus:ring-2 focus:ring-teal-50 outline-none placeholder:text-gray-400 placeholder:font-normal"
                />
                <p className="text-[10px] text-gray-400 mt-1.5">Shown as a teal pill on the TV leaderboard.</p>
              </>
            ) : (
              <p className={`text-[13px] leading-snug ${savedDailyGoalText.trim() ? "text-gray-600" : "text-gray-400 italic"}`}>
                {savedDailyGoalText.trim() || "No daily goal set."}
              </p>
            )}
          </div>
          {error && (
            <p className="text-sm font-medium text-pink-600 bg-pink-50 border border-pink-100 rounded-[10px] px-3 py-2">{error}</p>
          )}
        </div>
        <div className="flex gap-2.5 mt-[16px]">
          {canEdit && (
            <button
              type="button"
              onClick={handleSave}
              disabled={!isDirty || saving}
              className="flex-1 py-2.5 bg-[var(--gmb-primary)] text-white rounded-lg font-medium text-sm hover:brightness-95 transition-all disabled:opacity-40 disabled:pointer-events-none"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          )}
          <button type="button" onClick={onClose} className={`py-2.5 rounded-lg font-medium text-sm transition-all ${canEdit ? "flex-1 bg-gray-900 text-white hover:bg-black" : "w-full bg-[var(--gmb-primary)] text-white hover:brightness-95"}`}>Close</button>
        </div>
      </div>
    </div>
  );
}

function getLogoFromIntegrationApps(integrationApps, source) {
  if (!Array.isArray(integrationApps) || !source) return GMB_LOGO_URL;
  const matchingApp = integrationApps.find((app) => (app.code || app.id) === source);
  return matchingApp?.logo ? GET_IMAGE(matchingApp.logo) || matchingApp.logo : GMB_LOGO_URL;
}

/* ---- Per-visualization-type chart renderers (bar / progress / ring / gauge) ----
   Each visualization type in the appearance designer now renders its own shape,
   instead of every type collapsing into one line sparkline. */
function MiniBars({ data, color }) {
  const vals = (Array.isArray(data) ? data : []).map((d) => Number(d?.value)).filter((v) => Number.isFinite(v));
  if (vals.length < 2) return null;
  const pts = vals.slice(-12);
  const max = Math.max(...pts, 1);
  const n = pts.length;
  const gap = 3;
  const W = 300;
  const H = 88;
  const bw = (W - gap * (n - 1)) / n;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height={H} style={{ display: "block" }} aria-hidden>
      {pts.map((v, i) => {
        const h = Math.max(2, (Math.sqrt(Math.max(0, v)) / Math.sqrt(max)) * (H - 6));
        // eslint-disable-next-line react/no-array-index-key -- bars are positional, index is the identity
        return <rect key={i} x={i * (bw + gap)} y={H - h} width={bw} height={h} rx={2} fill={color} fillOpacity="0.85" />;
      })}
    </svg>
  );
}

function MiniProgress({ pct, color }) {
  const p = Math.round(pct * 100);
  return (
    <div className="w-full flex flex-col gap-1.5">
      <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${p}%`, background: color }} />
      </div>
      <span className="text-[10px] font-medium text-gray-400">{p}% of target</span>
    </div>
  );
}

function MiniRing({ pct, color }) {
  const r = 30;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(0, Math.min(1, pct)));
  return (
    <svg viewBox="0 0 80 80" width="84" height="84" className="mx-auto block" aria-hidden>
      <circle cx="40" cy="40" r={r} fill="none" stroke="#eef0f2" strokeWidth="8" />
      <circle cx="40" cy="40" r={r} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 40 40)" />
      <text x="40" y="45" textAnchor="middle" fontSize="16" fontWeight="600" fill="#0f172a">{Math.round(pct * 100)}%</text>
    </svg>
  );
}

function MiniGauge({ pct, color }) {
  const r = 36;
  const cx = 45;
  const cy = 44;
  const clamped = Math.max(0, Math.min(1, pct));
  const ang = Math.PI * (1 - clamped);
  const ex = cx + r * Math.cos(ang);
  const ey = cy - r * Math.sin(ang);
  return (
    <svg viewBox="0 0 90 58" width="100%" height="58" className="block" aria-hidden>
      <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="#eef0f2" strokeWidth="8" strokeLinecap="round" />
      <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${ex} ${ey}`} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" />
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize="13" fontWeight="600" fill="#0f172a">{Math.round(pct * 100)}%</text>
    </svg>
  );
}

/**
 * Company metrics card: aiDesign gamified style with graph, amount, and percentage.
 * No "per day" wording; shows current value, trend/percentage, and chart.
 */
function CompanyMetricCard({
  metric,
  userRole,
  onRemove,
  onEdit,
  onAssign,
  onGamifyForWorkspace,
  onSaveGuideText,
  onSaveDailyGoalText,
  isGlobalView = true,
  isPreview = false,
  assignedWorkspaces = [],
  allWorkspaces = [],
  integrationApps = [],
  allMetrics = [],
  rawKpi = null,
  rawKpis = [],
  togglePeriod = 3,
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const menuRef = useRef(null);

  const baseConfig = metric.visualConfig || DEFAULT_VISUAL_CONFIG;
  let appearanceType = baseConfig.type;
  if (typeof metric.appearance === "string") {
    appearanceType = metric.appearance;
  } else if (metric.appearance?.type) {
    appearanceType = metric.appearance.type;
  }
  const config = { ...DEFAULT_VISUAL_CONFIG, ...baseConfig, type: appearanceType };

  const { primaryColor: companyPrimaryColor } = UseCompanyColors();
  const isAdmin = userRole === "admin";
  const { appId, gameConfig } = metric;
  const isGamified = gameConfig?.status === "live";
  /** Integrated = from connected app (not manual); always use company color for graph */
  const isIntegrated = Boolean(appId && appId !== "manual") || metric._source === "integration" || metric._integration;

  const appById = APPS.find((a) => a.id === appId);
  const manualApp = APPS.find((a) => a.id === "manual");
  const sourceApp = appById || manualApp;
  const integrationApp = integrationApps.find((a) => (a.code || a.id) === appId);
  const appIcon = integrationApps.length > 0 && appId
    ? getLogoFromIntegrationApps(integrationApps, appId)
    : sourceApp?.icon;
  const appName = integrationApp?.name || sourceApp?.name || "Metric";
  const workspaceList = allWorkspaces.length > 0 ? allWorkspaces : WORKSPACES;

  const workspacesToShow = useMemo(() => {
    if (metric._source === "companyKpi" && metric.ownerWorkspaceId && metric.ownerWorkspaceId !== "GLOBAL") {
      const ws = workspaceList.find((w) => w.id === metric.ownerWorkspaceId);
      // Include every workspace this metric is also linked to (see
      // GroupKpi.linkedGroupIds / "Assign to workspace(s)") — not just its
      // home workspace, otherwise the pill silently drops workspaces the
      // metric is genuinely live on.
      const linkedIds = (rawKpi?.linkedGroupIds || []).map((id) => id?._id || id);
      const linkedWorkspaces = linkedIds
        .map((id) => workspaceList.find((w) => w.id === String(id)))
        .filter(Boolean);
      return [ws, ...linkedWorkspaces].filter(Boolean);
    }
    return assignedWorkspaces;
  }, [metric._source, metric.ownerWorkspaceId, workspaceList, assignedWorkspaces, rawKpi]);

  const formatValue = (val) => {
    if (metric.unit === "currency") return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(val);
    if (metric.unit === "percent") return `${Number(val).toFixed(1)}%`;
    return Number(val).toLocaleString();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setShowMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const data = Array.isArray(metric.sparklineData) && metric.sparklineData.length > 0
    ? metric.sparklineData
    : [{ timestamp: "1", value: metric.currentValue }];

  // Chart line uses a LIGHTER shade of the brand color so it sits as a soft
  // texture under the number, not a saturated foreground element. lighten(.25)
  // pulls the primary 25% toward white while keeping clear brand recognition.
  // A specific color picked in the appearance designer overrides the company
  // brand default; the "Brand" swatch (a CSS var) means "follow the company color".
  const pickedColor = config.primaryColor && config.primaryColor !== "var(--gmb-primary)" ? config.primaryColor : null;
  const baseColor = pickedColor || companyPrimaryColor || null;
  const color = baseColor ? lighten(baseColor, 0.25) : "var(--gmb-primary)";
  const trend = typeof metric.trend === "number" ? metric.trend : 0;

  const renderChart = () => {
    const t = config.type;
    if (t === "number" || t === "text-only") return null;
    // Progress / ring / gauge express "how far to target"; bar/line/area/spark
    // express the trend over time. Each visualization type renders its own shape.
    const curVal = Number(metric.currentValue) || 0;
    const targetVal = Number(metric.gameConfig?.dailyTarget) || Number(metric.gameConfig?.target) || (curVal > 0 ? curVal * 1.3 : 100);
    const pct = targetVal > 0 ? curVal / targetVal : 0;
    if (t === "bar") return <div className="w-full mt-4 h-[88px]"><MiniBars data={data} color={color} /></div>;
    if (t === "progress") return <div className="w-full mt-5 mb-1"><MiniProgress pct={pct} color={color} /></div>;
    if (t === "radial") return <div className="w-full mt-3 flex justify-center"><MiniRing pct={pct} color={color} /></div>;
    if (t === "thermometer") return <div className="w-full mt-4"><MiniGauge pct={pct} color={color} /></div>;
    // line / area / sparkline: shared smoothed chart; "line" drops the fill.
    return (
      <div className="w-full mt-4">
        <MetricSparkline data={data} color={color} height={88} gradient={t !== "line" && config.gradientEnabled !== false} opacity={typeof config.chartOpacity === "number" ? config.chartOpacity : 0.6} smooth={config.smoothLine !== false} />
      </div>
    );
  };

  const cornerClasses = { none: "rounded-none", default: "rounded-xl", large: "rounded-[32px]", full: "rounded-[48px]" };
  const shadowClasses = { none: "shadow-none", soft: "shadow-sm", medium: "shadow-md" };
  const cornerForCard = cornerClasses[config.cornerRadius || "default"];

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`bg-transparent transition-all group relative h-full min-w-0 flex flex-col ${cornerForCard} ${isPreview ? "pointer-events-none" : "cursor-pointer"}`}
    >
      <div
        className={`flex-grow flex flex-col bg-white border relative h-full min-w-0 transition-shadow duration-200 ${isGamified && !isPreview ? "border-[var(--gmb-primary-tint)] group-hover:border-[var(--gmb-primary-tint)]" : "border-gray-100"} ${cornerForCard} ${isPreview ? "" : "group-hover:shadow-md"}`}
      >
        <div className="p-6 flex flex-col h-full">
          <div className="flex justify-between items-start gap-2 mb-6 min-w-0">
            <div className="flex items-center space-x-4 overflow-hidden flex-grow min-w-0 min-w-[3.5rem] mr-0">
              {(sourceApp || appIcon) && (
                <div className="flex-shrink-0 w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center bg-gray-50 border border-gray-100 p-2">
                  <img src={appIcon} alt={appName} className="w-full h-full object-contain" />
                </div>
              )}
              <div className="flex flex-col overflow-hidden min-w-0 flex-grow">
                <h3 className="text-[14px] font-semibold text-gray-900 truncate leading-none">{metric.name}</h3>
              </div>
            </div>

            <div className="flex items-center gap-2 min-w-0 flex-shrink pt-1 overflow-visible">
              {isGamified && (
                <span
                  className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)]"
                  title="Gamified metric"
                  aria-label="Gamified metric"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                    <path d="M4 22h16" />
                    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
                    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
                    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
                  </svg>
                </span>
              )}

              {!isPreview && (
                <div className="relative flex-shrink-0" ref={menuRef}>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors opacity-40 group-hover:opacity-100" aria-label="Open metric menu">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-5 h-5">
                      <circle cx="12" cy="12" r="1" />
                      <circle cx="12" cy="5" r="1" />
                      <circle cx="12" cy="19" r="1" />
                    </svg>
                  </button>
                  {showMenu && (
                    <div className="absolute right-0 mt-2 w-64 rounded-lg py-2 z-[50] overflow-hidden" style={FROSTED_POPOVER_STYLE}>
                      {isGamified && (
                        <button type="button" onClick={(e) => { e.stopPropagation(); setShowRules(true); setShowMenu(false); }} className="w-full flex items-center px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-3 text-gray-400">
                            <line x1="6" x2="10" y1="11" y2="11" />
                            <line x1="8" x2="8" y1="9" y2="13" />
                            <line x1="15" x2="15.01" y1="13" y2="13" />
                            <line x1="18" x2="18.01" y1="11" y2="11" />
                            <path d="M17.32 5H6.68a4 4 0 0 0-3.978 3.59c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.544-.604-6.584-.685-7.258-.007-.05-.011-.1-.017-.151A4 4 0 0 0 17.32 5z" />
                          </svg>
                          Game rules
                        </button>
                      )}
                      {isAdmin && (
                        <>
                          {/* Appearance / Gamify / Assign / Remove are mutations — the
                              modals & flows behind them aren't built for mobile, so they
                              stay desktop-only. "Metric details" is read-only and stays
                              visible everywhere. */}
                          <button type="button" onClick={() => { onEdit?.("appearance"); setShowMenu(false); }} className="w-full hidden lg:flex items-center px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-3 text-gray-400">
                              <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                            </svg>
                            Appearance
                          </button>
                          <button type="button" onClick={(e) => { e.stopPropagation(); setShowDetails(true); setShowMenu(false); }} className="w-full flex items-center px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-3 text-gray-400">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M12 16v-4M12 8h.01" />
                            </svg>
                            Metric details
                          </button>
                          <button type="button" onClick={() => { onGamifyForWorkspace?.(); setShowMenu(false); }} className="w-full hidden lg:flex items-center px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-3 text-gray-400">
                              <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                              <path d="M20 3v4" />
                              <path d="M22 5h-4" />
                              <path d="M4 17v2" />
                              <path d="M5 18H3" />
                            </svg>
                            Gamify for workspace
                          </button>
                          <button type="button" onClick={() => { onAssign?.(); setShowMenu(false); }} className="w-full hidden lg:flex items-center px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left border-t border-gray-50 mt-1">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-3 text-gray-400">
                              <path d="M12 4v16m8-8H4" />
                            </svg>
                            Assign to workspace
                          </button>
                          <button type="button" onClick={() => { onRemove?.(); setShowMenu(false); }} className="w-full hidden lg:flex items-center px-4 py-2.5 text-sm font-medium text-pink-600 hover:bg-pink-50 transition-colors text-left">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-3">
                              <path d="M3 6h18" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                            Remove metric
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex-grow flex flex-col justify-center">
            <div className="flex flex-col items-start">
              <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                <span className="font-semibold text-gray-900 text-[24px] leading-none">
                  {formatValue(metric.currentValue)}
                </span>
                {config.showTrend && typeof trend === "number" && (
                  <span className={`text-[12px] leading-[18px] font-semibold flex items-center ${trend >= 0 ? "text-emerald-500" : "text-pink-500"}`}>
                    {config.showArrow && (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className={`w-3 h-3 mr-0.5 ${trend < 0 ? "rotate-180" : ""}`}>
                        <path d="M5 15l7-7 7 7" />
                      </svg>
                    )}
                    {Math.abs(trend)}%
                  </span>
                )}
              </div>
              {config.showDescription && metric.description && (
                <p className="text-[11px] leading-[16px] text-gray-500 line-clamp-2 h-8">{metric.description}</p>
              )}
              {renderChart()}
            </div>
          </div>
        </div>

        {!isPreview && isGlobalView && isAdmin && (
          <div
            className="absolute inset-x-0 bottom-0 h-10 px-4 bg-white/95 backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-between gap-3 border-t border-gray-100 rounded-bl-2xl rounded-br-2xl"
            style={{ fontFamily: "DM Sans" }}
          >
            {/* Assignments — flat tag list */}
            <div className="flex items-center gap-1.5 overflow-hidden min-w-0 flex-1">
              {workspacesToShow.length > 0 ? (
                <>
                  <span className="flex-shrink-0 text-[10px] leading-[16px] font-medium text-gray-400">
                    Assigned to
                  </span>
                  <div className="flex items-center gap-1 overflow-hidden min-w-0">
                    {workspacesToShow.slice(0, 2).map((ws) => (
                      <span
                        key={`${metric.dashboardId}-ws-chip-${ws.id}`}
                        className="flex-shrink-0 px-2 py-0.5 rounded-md bg-[var(--gmb-primary-soft)] text-[11px] leading-[16px] font-medium text-[var(--gmb-primary)] whitespace-nowrap truncate max-w-[110px]"
                      >
                        {ws.name}
                      </span>
                    ))}
                    {workspacesToShow.length > 2 && (
                      <span className="flex-shrink-0 px-1.5 py-0.5 rounded-md bg-gray-50 text-[11px] leading-[16px] font-medium text-gray-500 whitespace-nowrap">
                        +{workspacesToShow.length - 2}
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <span className="text-[11px] leading-[16px] text-gray-400">Not assigned to any workspace</span>
              )}
            </div>

            {/* Assign — quiet secondary action, desktop-only (mutation) */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onAssign?.(); }}
              className="flex-shrink-0 hidden lg:flex items-center gap-1 text-[12px] leading-[18px] font-medium text-gray-600 hover:text-[var(--gmb-primary)] transition-colors"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                <path d="M12 5v14M5 12h14" />
              </svg>
              <span>{workspacesToShow.length > 0 ? "Manage" : "Assign"}</span>
            </button>
          </div>
        )}
      </div>

      <CompanyRuleModal
        isOpen={showRules}
        onClose={() => setShowRules(false)}
        metric={metric}
        canEdit={isAdmin && !isPreview}
        onSaveGuideText={onSaveGuideText}
        onSaveDailyGoalText={onSaveDailyGoalText}
      />
      <MetricDetailsModal
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        metric={metric}
        allMetrics={allMetrics}
        rawKpi={rawKpi}
        rawKpis={rawKpis}
        integrationApps={integrationApps}
      />

      <style>{`
        .rotate-y-active { animation: spin-y-smooth 2.5s linear infinite; }
        @keyframes spin-y-smooth { from { transform: perspective(400px) rotateY(0deg); } to { transform: perspective(400px) rotateY(360deg); } }
      `}</style>
    </div>
  );
}

export default CompanyMetricCard;
